import { Component, inject, OnInit, signal, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AssetInstance } from '../../core/models/asset-instance.model';
import { CheckPoint } from '../../core/models/checkPoint.model';
import { Form } from '../../core/models/form.model';
import { AssetInstanceService } from '../../core/services/asset-instance.service';
import { BreadcrumbService } from '../../core/services/breadcrumb.service';
import { CheckPointService } from '../../core/services/checkPoint.service';
import { FormService } from '../../core/services/form.service';
import { LoggerService } from '../../core/services/logger.service';
import { NfcService } from '../../core/services/nfc.service';
import { ButtonComponent } from '../../shared/components/button/button.component';
import { CardContentComponent } from '../../shared/components/card/card-content.component';
import { CardHeaderComponent } from '../../shared/components/card/card-header.component';
import { CardTitleComponent } from '../../shared/components/card/card-title.component';
import { CardComponent } from '../../shared/components/card/card.component';
import { CheckpointFormHeaderComponent } from '../../shared/components/checkpoint-form-header/checkpoint-form-header.component';
import { IconComponent } from '../../shared/components/icon/icon.component';
import { SpinnerComponent } from '../../shared/components/spinner/spinner.component';
import { HasPermissionDirective } from '../../shared/directives/has-permission.directive';
import { TranslatePipe } from '../../shared/pipes/translate.pipe';

@Component({
  selector: 'app-form-view',
  standalone: true,
  imports: [
    CardComponent,
    CardHeaderComponent,
    CardTitleComponent,
    CardContentComponent,
    CheckpointFormHeaderComponent,
    IconComponent,
    ButtonComponent,
    SpinnerComponent,
    HasPermissionDirective,
    TranslatePipe,
  ],
  templateUrl: './form-view.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './form-view.component.css',
})
export class FormViewComponent implements OnInit {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private checkPointService = inject(CheckPointService);
  private formService = inject(FormService);
  private assetInstanceService = inject(AssetInstanceService);
  private breadcrumbService = inject(BreadcrumbService);
  private logger = inject(LoggerService).getLogger('FormViewComponent');
  private nfc = inject(NfcService);

  /** Offer tag writing where the device has NFC (even if switched off — the page explains) */
  showNfcTags = signal(false);

  loading = signal(true);
  error = signal<string | null>(null);
  checkPoint = signal<CheckPoint | null>(null);
  form = signal<Form | null>(null);
  instance = signal<AssetInstance | null>(null);

  checkpointId!: string;
  formId!: string;
  instanceId: string | null = null;

  async ngOnInit() {
    this.nfc.getAvailability().then((a) => this.showNfcTags.set(a.available || a.reason === 'disabled'));
    this.checkpointId = this.route.snapshot.paramMap.get('checkpointId') ?? '';
    this.formId = this.route.snapshot.paramMap.get('formId') ?? '';
    this.instanceId = this.route.snapshot.paramMap.get('instanceId');
    await this.loadData();
  }

  private async loadData() {
    this.loading.set(true);
    this.error.set(null);

    try {
      const [checkPoint, form] = await Promise.all([
        this.checkPointService.getById(this.checkpointId),
        this.formService.getById(this.formId),
      ]);

      this.checkPoint.set(checkPoint);
      this.form.set(form);

      if (this.instanceId) {
        const instance = await this.assetInstanceService.getById(this.instanceId);
        this.instance.set(instance);
      }

      const dynamicLabels = [checkPoint?.name ?? '', form?.name ?? ''];
      if (this.instance()) {
        dynamicLabels.push(`#${this.instance()!.serial_number}`);
      }
      this.breadcrumbService.setDynamicLabels(dynamicLabels);
    } catch (err) {
      this.logger.error('Failed to load form view data', err);
      this.error.set('Failed to load form details.');
    } finally {
      this.loading.set(false);
    }
  }

  fillOutForm() {
    const segments: (string | null)[] = ['/submission', this.checkpointId, this.formId];
    if (this.instanceId) segments.push(this.instanceId);
    this.router.navigate(segments as string[], {
      queryParams: { returnUrl: this.router.url },
    });
  }

  viewHistory() {
    const segments = ['/submission', this.checkpointId, this.formId];
    if (this.instanceId) segments.push(this.instanceId);
    segments.push('history');
    this.router.navigate(segments);
  }

  printLabels() {
    this.router.navigate(['/checkpoints', this.checkpointId, this.formId, 'labels']);
  }

  writeNfcTags() {
    this.router.navigate(['/checkpoints', this.checkpointId, this.formId, 'nfc']);
  }

  editForm() {
    this.router.navigate(['/forms', this.formId]);
  }
}
